import { Select as Base } from '@base-ui/react/select'

import { Icon } from './Icon'

/**
 * One value out of a short, fixed list.
 *
 * **Not a row of buttons and not a bare `<select>`.** A row of buttons is a row of *actions*, and it
 * grows a column every time the list does; the browser's own control brings none of MyLib's
 * typography, spacing or colour with it. Picking one of several is its own question and gets its own
 * shape — see the design chapter, "The form follows the question".
 *
 * **Built on Base UI, dressed here.** What comes from the library is the part that is tedious to get
 * right and invisible when it is wrong: keyboard navigation, typeahead, focus returning to the
 * trigger, the popup staying on screen, touch. What comes from MyLib is everything anybody sees — the
 * tokens, the sizes, the one accent — because a control that looked like a library's default would be
 * the same failure as the bare `<select>` one step further along.
 */
export interface SelectOption {
  readonly value: string
  readonly label: string
}

export interface SelectProps {
  /** What is being chosen, as a thing rather than a command: "Markup". Always visible. */
  readonly label: string
  readonly value: string
  readonly options: readonly SelectOption[]
  readonly onChange: (value: string) => void
  readonly disabled?: boolean
  /** One sentence under it, where the choice needs a word of explanation. */
  readonly hint?: string
  /**
   * Keeps the label as the control's *name* without drawing it above the trigger.
   *
   * For a select that stands in a row of buttons, where a label on its own line would make the
   * control two lines tall and knock the row out of alignment — and where the value in the trigger
   * already says what it is. The accessible name is unchanged, so nothing is lost to a screen reader.
   */
  readonly labelHidden?: boolean
  /**
   * Takes the trigger out of the ordinary Tab sequence, without hiding it.
   *
   * For a pair of short, consecutive editors: Tab there should move between the fields somebody is
   * filling in, not stop at a display preference between them. The control stays visible, clickable
   * and reachable — see `RichText`, which is the only caller that asks for this.
   */
  readonly tabIndex?: number
}

export function Select({
  label,
  value,
  options,
  onChange,
  disabled = false,
  hint,
  labelHidden = false,
  tabIndex,
}: SelectProps) {
  return (
    <Base.Root
      value={value}
      // What lets the trigger show "AsciiDoc" rather than `text/asciidoc`: the value is a media type
      // and nobody reads media types.
      items={options as SelectOption[]}
      disabled={disabled}
      onValueChange={(chosen) => {
        // `null` is what a Select answers with when it is cleared, which this one never is: there is
        // always exactly one value, so anything else is not a choice somebody made.
        if (typeof chosen === 'string' && chosen !== value) {
          onChange(chosen)
        }
      }}
    >
      <div className={labelHidden ? 'select select--inline' : 'select'}>
        <Base.Label className={labelHidden ? 'visually-hidden' : 'select__label'}>
          {label}
        </Base.Label>
        <Base.Trigger className="select__trigger" tabIndex={tabIndex}>
          <Base.Value className="select__value" />
          <Base.Icon className="select__mark">
            <Icon name="down" />
          </Base.Icon>
        </Base.Trigger>
        {hint !== undefined && <p className="select__hint">{hint}</p>}
        <Base.Portal>
          <Base.Positioner className="select__positioner" sideOffset={4}>
            <Base.Popup className="select__popup">
              <Base.List className="select__list">
                {options.map((option) => (
                  <Base.Item key={option.value} value={option.value} className="select__item">
                    {/*
                      The tick sits in a column of its own so the labels line up whether or not one
                      of them is chosen — a list whose text shifts sideways as the choice moves is a
                      list nobody can scan.
                    */}
                    <Base.ItemIndicator className="select__ticked">
                      <Icon name="check" />
                    </Base.ItemIndicator>
                    <Base.ItemText className="select__item-text">{option.label}</Base.ItemText>
                  </Base.Item>
                ))}
              </Base.List>
            </Base.Popup>
          </Base.Positioner>
        </Base.Portal>
      </div>
    </Base.Root>
  )
}
